import {
  applyTransformations,
  TransformationTag,
} from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { getTotalAmountOfItem } from "../items/Inventory";
import { getItemById } from "../items/Items";
import { PlayerContextState } from "../PlayerContext";
import { getResourceAmount, Resource } from "../Resources";
import { hasTemporaryEffect } from "../timetick/TemporaryEffects";

export type DoActionArgs = { isAutomatic: boolean };

export enum ActionSubcategory {
  Gather = "Gather",
  Mana = "Mana",
  Production = "Production",
  Storage = "Storage",
  Electricity = "Electricity",
  Ancient = "Ancient",
  Housing = "Housing",
  "Use Time Pieces" = "Use Time Pieces",
}

export interface Action {
  getName(): string;
  getArea(): string | string[];
  getCategory(): string;
  getSubcategory(): ActionSubcategory | undefined;
  getDisplayName(state: PlayerContextState): string;
  getCost(state: PlayerContextState): {
    resources: Partial<Record<Resource, number>>;
    items: Record<string, number>;
  };
  isEnabled(state: PlayerContextState): boolean;
  isActive(state: PlayerContextState): boolean;
  canSpam(): boolean;
  doAction(args: DoActionArgs, state: PlayerContextState): PlayerContextState;
  isVisible(state: PlayerContextState): boolean;
  getDisplayDescription(state: PlayerContextState): string;
  getDisplayEffect(state: PlayerContextState): string;
}

export type ActionEffect = {
  value: number;
  tags?: TransformationTag[];
};

export abstract class ActionBase implements Action {
  constructor() {}

  abstract getName(): string;
  abstract getArea(): string | string[];
  abstract getCost(state: PlayerContextState): {
    resources: Partial<Record<Resource, number>>;
    items: Record<string, number>;
  };
  abstract doAction(
    args: DoActionArgs,
    state: PlayerContextState,
  ): PlayerContextState;
  abstract isVisible(state: PlayerContextState): boolean;
  abstract getDisplayDescription(state: PlayerContextState): string;
  abstract getDisplayEffect(state: PlayerContextState): string;

  getId(): string {
    return this.getName();
  }

  getDisplayName(state: PlayerContextState): string {
    return this.getName();
  }

  canSpam(): boolean {
    return true;
  }

  protected getBaseActionEffects(): Record<string, ActionEffect> {
    return {};
  }

  getSubcategory(): ActionSubcategory | undefined {
    return undefined;
  }

  tagsByEffect: Record<string, string[]> = {};
  getTagsByEffect(key: string, baseEffect: ActionEffect): string[] {
    if (this.tagsByEffect?.[key] == null) {
      this.tagsByEffect[key] = [
        ...this.getTags(),
        ...(baseEffect.tags ?? []),
        TransformationTags.ActionEffect,
      ];
    }
    return this.tagsByEffect[key];
  }

  getActionEffects(
    state: PlayerContextState,
    onlyId?: string,
  ): Record<string, number> {
    const baseEffects = this.getBaseActionEffects();
    const finalEffects: Record<string, number> = {};
    for (let key in baseEffects) {
      if (onlyId != null && key != onlyId) {
        continue;
      }
      const baseEffect = baseEffects[key];
      finalEffects[key] = applyTransformations(
        this.getTagsByEffect(key, baseEffect),
        state,
        baseEffect.value,
        {
          action: this,
        },
      );
    }
    return finalEffects;
  }

  getTags(): TransformationTag[] {
    const tags = [
      TransformationTags.Action,
      this.getCategoryTag(),
      this.getAreaTag(),
      this.getId(),
    ];
    const subcategoryTag = this.getSubcategoryTag();
    if (subcategoryTag != null) {
      tags.push(subcategoryTag);
    }
    return tags;
  }

  getCategory(): string {
    return "Actions";
  }

  getCategoryTag(): string {
    return "Category:" + this.getCategory();
  }

  getSubcategoryTag(): string | undefined {
    if (this.getSubcategory() != null) {
      return "Subcategory:" + this.getSubcategory();
    }
    return undefined;
  }

  getAreaTag(): string {
    return "Area:" + this.getArea();
  }

  canAfford(state: PlayerContextState): boolean {
    const cost = this.getCost(state);
    return (
      (Object.keys(cost.resources) as Array<Resource>).every(
        (resource) =>
          getResourceAmount(state, resource) >= (cost.resources[resource] ?? 0),
      ) &&
      (Object.keys(cost.items) as Array<string>).every(
        (itemId) =>
          getTotalAmountOfItem(getItemById(itemId), state) >=
          cost.items[itemId],
      )
    );
  }

  isEnabled(state: PlayerContextState): boolean {
    return this.canAfford(state);
  }

  // Returns true if the action is considered "active".
  // Used mainly for spells that give buffs, to display a visual aid.
  isActive(state: PlayerContextState): boolean {
    return hasTemporaryEffect(state, this.getName());
  }
}
